import React, { useState, useMemo, useEffect, useCallback, lazy, Suspense } from "react";
import "./App.css";
import CalendlyModal from './CalendlyModal';

// Lazy load modals - they're not needed on initial page load
const ContactModal = lazy(() => import('./ContactModal'));
const GeneralContactModal = lazy(() => import('./GeneralContactModal'));
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Switch } from "./components/ui/switch";
import { Label } from "./components/ui/label";
import * as analytics from './analytics';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import { 
  Search, 
  Star, 
  Linkedin, 
  Twitter, 
  Facebook, 
  Instagram, 
  Youtube,
  TrendingUp,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Settings,
  X,
  Mail,
  ExternalLink,
  Phone,
  Send,
  User,
  MessageSquare,
  Calculator,
  Percent,
  ChevronDown,
  LogIn,
  Loader2,
  Menu
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { BACKEND_URL } from './config';

// App version for cache busting - increment when rating logic changes
const APP_VERSION = '1.2.0';

// Default weights for each metric (equal distribution)
const DEFAULT_WEIGHTS = {
  '1_month': 12.5,
  '3_month': 12.5,
  '6_month': 12.5,
  '1_year': 12.5,
  '2_year': 12.5,
  '3_year': 12.5,
  '5_year': 12.5,
  'since_inception': 12.5
};

const StarRating = ({ rating }) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 !== 0;
  
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(5)].map((_, index) => (
        <Star
          key={index}
          className={`w-4 h-4 ${
            index < fullStars
              ? "fill-amber-400 text-amber-400"
              : index === fullStars && hasHalfStar
              ? "fill-amber-400 text-amber-400 half-star"
              : "text-gray-300"
          }`}
          style={index === fullStars && hasHalfStar ? {
            clipPath: "polygon(0 0, 50% 0, 50% 100%, 0 100%)"
          } : {}}
        />
      ))}
    </div>
  );
};

// Helper function to format performance values
// Returns blank string if value is 0, otherwise returns formatted value
// Note: The actual 0 values are still used in calculations (like weighted scores)
const formatPerformanceValue = (value, decimals = 1) => {
  // Return empty string if value is null, undefined, empty string, or any form of 0
  if (!value || value === '' || value === null || value === undefined) {
    return '';
  }
  
  const numValue = parseFloat(value);
  
  // Check if parsed value is NaN or 0 (this catches "0", "0.0", 0, etc.)
  if (isNaN(numValue) || numValue === 0) {
    return '';
  }
  
  return `${numValue.toFixed(decimals)}%`;
};

// Helper function to format AUM values
const formatAUM = (value) => {
  const numValue = parseFloat(value) || 0;
  if (numValue === 0) {
    return '-';
  }
  return numValue.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

// Helper function to format scheme names from ALL CAPS to proper title case
const formatSchemeName = (name) => {
  if (!name) return name;
  
  // Convert to lowercase first, then capitalize each word
  return name
    .toLowerCase()
    .split(' ')
    .map(word => {
      // Handle special cases like "PMS", "AMC", etc.
      const upperWords = ['pms', 'amc', 'llp', 'ltd', 'pvt', 'aif', 'nri'];
      if (upperWords.includes(word)) {
        return word.toUpperCase();
      }
      // Capitalize first letter of each word
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

function LandingPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [comparedItems, setComparedItems] = useState({});
  const [email, setEmail] = useState("");
  const [amcData, setAmcData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showWeightModal, setShowWeightModal] = useState(false);
  const [weights, setWeights] = useState(DEFAULT_WEIGHTS);
  const [tempWeights, setTempWeights] = useState(DEFAULT_WEIGHTS);
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [showSchemeModal, setShowSchemeModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    mobile: '',
    subject: '',
    message: ''
  });
  const [sendingContact, setSendingContact] = useState(false);
  const [schemeContacts, setSchemeContacts] = useState(null);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 50;
  const [enrichedSchemes, setEnrichedSchemes] = useState({});
  const [showGeneralContactModal, setShowGeneralContactModal] = useState(false);
  const [showFeeCalculatorModal, setShowFeeCalculatorModal] = useState(false);
  const [showCalendlyModal, setShowCalendlyModal] = useState(false);
  const [feeOptions, setFeeOptions] = useState({
    option1: { fixedFee: '', hurdle: '', performanceSharing: '' },
    option2: { fixedFee: '', hurdle: '', performanceSharing: '' },
    option3: { fixedFee: '', hurdle: '', performanceSharing: '' }
  });
  const [feeChartData, setFeeChartData] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const [showLoginDropdown, setShowLoginDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [schemeFilter, setSchemeFilter] = useState('all'); // 'all', 'pms', 'aif'
  const [minAum, setMinAum] = useState(10);
  const [maxAum, setMaxAum] = useState(30000);


  // Check version and clear cache if needed
  useEffect(() => {
    const storedVersion = localStorage.getItem('appVersion');
    if (storedVersion !== APP_VERSION) {
      // Version changed - clear all cached data
      console.log(`App version updated from ${storedVersion} to ${APP_VERSION} - clearing cache`);
      localStorage.clear();
      sessionStorage.clear();
      localStorage.setItem('appVersion', APP_VERSION);
      
      // Force reload to get fresh data
      window.location.reload();
    }
  }, []);

  // Fetch available months
  const fetchAvailableMonths = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/available-months`);
      const result = await response.json();
      
      if (result.available_months && result.available_months.length > 0) {
        setAvailableMonths(result.available_months);
        setSelectedMonth(result.current_month);
      }
    } catch (error) {
      console.error('Error fetching available months:', error);
    }
  };

  // Fetch real data from API
  const fetchData = async (year = null, month = null, customWeights = null) => {
    try {
      // Build URL with weights parameter for backend rating calculation
      let url = `${BACKEND_URL}/api/performance-data-display`;
      const params = new URLSearchParams();
      
      if (year !== null && month !== null) {
        params.append('year', year);
        params.append('month', month);
      }
      
      // Use custom weights if provided, otherwise use current weights state
      const weightsToUse = customWeights || weights;
      
      // Add weights as comma-separated string in correct order
      const weightsArray = [
        weightsToUse['1_month'],
        weightsToUse['3_month'],
        weightsToUse['6_month'],
        weightsToUse['1_year'],
        weightsToUse['2_year'],
        weightsToUse['3_year'],
        weightsToUse['5_year'],
        weightsToUse['since_inception']
      ];
      const weightsString = weightsArray.join(',');
      params.append('weights', weightsString);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const cacheKey = `perf_data_${weightsString}_${year}_${month}`;
      
      // If custom weights are provided (user changed weights), clear all cached data
      // This forces fresh calculation and display of new star ratings
      if (customWeights) {
        console.log('Weights changed - clearing all cached data to force recalculation');
        sessionStorage.clear();
      } else {
        // Show cached data immediately if available (optimistic UI) - only for same weights
        const cachedData = sessionStorage.getItem(cacheKey);
        if (cachedData && amcData.length === 0) {
          try {
            const parsed = JSON.parse(cachedData);
            setAmcData(parsed);
          } catch (e) {
            console.error('Error parsing cached data:', e);
          }
        }
      }
      
      setLoading(true);
      
      // Fetch fresh data
      const response = await fetch(url);
      const result = await response.json();
      
      if (result.status === 'success') {
        // Data now includes pre-calculated ratings from backend
        setAmcData(result.data);
        
        // Cache the data for instant display on next load
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(result.data));
        } catch (e) {
          // Ignore quota exceeded errors
          console.warn('SessionStorage quota exceeded, skipping cache');
        }
      }
    } catch (error) {
      console.error('Error fetching performance data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load weights from localStorage on mount
  useEffect(() => {
    const savedWeights = localStorage.getItem('amcWeights');
    if (savedWeights) {
      try {
        const parsed = JSON.parse(savedWeights);
        setWeights(parsed);
        setTempWeights(parsed);
      } catch (e) {
        console.error('Error loading weights:', e);
      }
    }
  }, []);

  // Initial fetch on mount - fetch available months first
  useEffect(() => {
    fetchAvailableMonths();
  }, []);

  // Fetch data when selected month changes
  useEffect(() => {
    if (selectedMonth) {
      fetchData(selectedMonth.year, selectedMonth.month);
    }
  }, [selectedMonth]);

  // Monitor auto-refresh setting from localStorage
  useEffect(() => {
    const checkAutoRefresh = () => {
      const enabled = localStorage.getItem('autoRefreshEnabled') === 'true';
      setAutoRefreshEnabled(enabled);
    };

    // Check initially
    checkAutoRefresh();

    // Listen for storage changes (when admin panel toggles it)
    window.addEventListener('storage', checkAutoRefresh);
    
    // Also check periodically in case it's changed in another tab
    const interval = setInterval(checkAutoRefresh, 1000);

    return () => {
      window.removeEventListener('storage', checkAutoRefresh);
      clearInterval(interval);
    };
  }, []);

  // Auto-refresh data every 60 seconds if enabled
  useEffect(() => {
    if (autoRefreshEnabled && selectedMonth) {
      const interval = setInterval(() => {
        console.log('Auto-refreshing landing page data...');
        fetchData(selectedMonth.year, selectedMonth.month);
      }, 60000); // 60 seconds

      return () => clearInterval(interval);
    }
  }, [autoRefreshEnabled, selectedMonth]);

  // Handle ESC key to close modals
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape') {
        if (showFeeCalculatorModal) {
          setShowFeeCalculatorModal(false);
          setFeeOptions({
            option1: { fixedFee: '', hurdle: '', performanceSharing: '' },
            option2: { fixedFee: '', hurdle: '', performanceSharing: '' },
            option3: { fixedFee: '', hurdle: '', performanceSharing: '' }
          });
          setFeeChartData([]);
        }
        if (showGeneralContactModal) {
          setShowGeneralContactModal(false);
          setGeneralContactForm({ name: '', mobile: '', body: '' });
        }
        if (showWeightModal) {
          analytics.trackWeightModalClose();
          setShowWeightModal(false);
          setTempWeights(weights);
        }
        if (showSchemeModal) {
          analytics.trackSchemeModalClose();
          setShowSchemeModal(false);
        }
        if (showContactModal) {
          analytics.trackContactModalClose();
          setShowContactModal(false);
        }
        if (showCompareModal) {
          analytics.trackCompareModalClose();
          setShowCompareModal(false);
        }
        if (showMobileMenu) {
          setShowMobileMenu(false);
        }
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [showFeeCalculatorModal, showGeneralContactModal, showWeightModal, showSchemeModal, showContactModal, showCompareModal, showMobileMenu, weights]);

  const handleSort = useCallback((key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  const getSortIcon = useCallback((columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="w-4 h-4 text-slate-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-4 h-4 text-teal-600" />
      : <ArrowDown className="w-4 h-4 text-teal-600" />;
  }, [sortConfig]);

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page on search
    
    // Track search only if query is not empty
    if (query.trim()) {
      analytics.trackSearch(query);
    }
  };

  // Calculate weighted score for a fund
  const calculateWeightedScore = (fund) => {
    // Normalize weights if total > 100
    const totalWeight = Object.values(weights).reduce((sum, w) => sum + w, 0);
    const normalizedWeights = totalWeight > 100 
      ? Object.fromEntries(Object.entries(weights).map(([k, v]) => [k, (v / totalWeight) * 100]))
      : weights;

    // Calculate weighted sum
    let weightedSum = 0;
    Object.entries(normalizedWeights).forEach(([key, weight]) => {
      const value = parseFloat(fund[key]) || 0;
      weightedSum += (weight / 100) * value;
    });

    return weightedSum;
  };

  // Calculate percentile for a score among all scores
  const calculatePercentile = (score, allScores) => {
    const sorted = [...allScores].sort((a, b) => a - b);
    const index = sorted.findIndex(s => s >= score);
    if (index === -1) return 100;
    return (index / sorted.length) * 100;
  };

  // Get star rating based on percentile
  const getStarRating = (percentile) => {
    if (percentile >= 90) return 5;
    if (percentile >= 80) return 4.5;
    if (percentile >= 70) return 4;
    if (percentile >= 60) return 3.5;
    if (percentile >= 50) return 3;
    if (percentile >= 40) return 2.5;
    if (percentile >= 30) return 2;
    if (percentile >= 20) return 1.5;
    if (percentile >= 10) return 1;
    return 0.5;
  };

  // Funds already have ratings calculated by backend
  // No need for heavy frontend calculations anymore
  const fundsWithRatings = useMemo(() => {
    // Backend now includes rating, percentile, and weighted_score
    // Just return the data as-is
    return amcData;
  }, [amcData]);

  const filteredData = useMemo(() => {
    let filtered = fundsWithRatings.filter(item => {
      const matchesSearch = 
        item.amc_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.scheme_name.toLowerCase().includes(searchQuery.toLowerCase());
      
      // Apply scheme type filter
      const matchesFilter = 
        schemeFilter === 'all' ? true :
        schemeFilter === 'aif' ? item.scheme_category === 'AIF' :
        schemeFilter === 'pms' ? item.scheme_category !== 'AIF' : true;
      
      // Apply AUM filter
      const itemAum = parseFloat(item.aum) || 0;
      const matchesAum = itemAum >= minAum && itemAum <= maxAum;
      
      return matchesSearch && matchesFilter && matchesAum;
    });

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Convert to numbers for numeric columns
        if (['aum', '1_month', '3_month', '6_month', '1_year', '2_year', '3_year', '5_year', 'since_inception', 'rating'].includes(sortConfig.key)) {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        } else {
          // For text columns
          aValue = String(aValue || '').toLowerCase();
          bValue = String(bValue || '').toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [searchQuery, fundsWithRatings, sortConfig, schemeFilter, minAum, maxAum]);

  const handleCompareToggle = (id) => {
    const currentCount = Object.values(comparedItems).filter(Boolean).length;
    
    // If trying to add more than 5, show error
    if (!comparedItems[id] && currentCount >= 5) {
      toast.error("Maximum 5 schemes can be compared at once");
      return;
    }
    
    // Track add/remove from compare
    if (!comparedItems[id]) {
      analytics.trackCompareScheme(id);
    } else {
      analytics.trackRemoveFromCompare(id);
    }
    
    setComparedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleOpenCompare = () => {
    if (comparedCount === 0) {
      toast.error("Please select at least one scheme to compare");
      return;
    }
    analytics.trackCompareModalOpen(comparedCount);
    setShowCompareModal(true);
  };

  const getComparedSchemes = () => {
    return fundsWithRatings.filter(fund => comparedItems[`${fund.amc_name}-${fund.scheme_name}`]);
  };

  // Paginate the filtered/sorted data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, ITEMS_PER_PAGE]);

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);

  const handleCalculateFees = () => {
    // Check if at least one option has a fixed fee (only requirement)
    const option1Filled = feeOptions.option1.fixedFee && feeOptions.option1.fixedFee.trim() !== '';
    const option2Filled = feeOptions.option2.fixedFee && feeOptions.option2.fixedFee.trim() !== '';
    const option3Filled = feeOptions.option3.fixedFee && feeOptions.option3.fixedFee.trim() !== '';
    
    if (!option1Filled && !option2Filled && !option3Filled) {
      toast.error("Please enter at least one fixed fee");
      return;
    }
    
    // Generate returns from -10% to 30% in 2% increments
    const returns = [];
    for (let i = -10; i <= 30; i += 2) {
      returns.push(i);
    }
    
    // Calculate fees for each return and each option
    const chartData = returns.map(returnValue => {
      const dataPoint = { return: returnValue };
      
      // Calculate for Option 1
      if (option1Filled) {
        const fixed1 = parseFloat(feeOptions.option1.fixedFee) || 0;
        const hurdle1 = parseFloat(feeOptions.option1.hurdle) || 0;
        const perfSharing1 = parseFloat(feeOptions.option1.performanceSharing) || 0;
        const performanceFee1 = Math.max(0, (returnValue - hurdle1 - fixed1)) * (perfSharing1 / 100);
        dataPoint.option1 = fixed1 + performanceFee1;
      }
      
      // Calculate for Option 2
      if (option2Filled) {
        const fixed2 = parseFloat(feeOptions.option2.fixedFee) || 0;
        const hurdle2 = parseFloat(feeOptions.option2.hurdle) || 0;
        const perfSharing2 = parseFloat(feeOptions.option2.performanceSharing) || 0;
        const performanceFee2 = Math.max(0, (returnValue - hurdle2 - fixed2)) * (perfSharing2 / 100);
        dataPoint.option2 = fixed2 + performanceFee2;
      }
      
      // Calculate for Option 3
      if (option3Filled) {
        const fixed3 = parseFloat(feeOptions.option3.fixedFee) || 0;
        const hurdle3 = parseFloat(feeOptions.option3.hurdle) || 0;
        const perfSharing3 = parseFloat(feeOptions.option3.performanceSharing) || 0;
        const performanceFee3 = Math.max(0, (returnValue - hurdle3 - fixed3)) * (perfSharing3 / 100);
        dataPoint.option3 = fixed3 + performanceFee3;
      }
      
      return dataPoint;
    });
    
    setFeeChartData(chartData);
    toast.success("Fee payoff chart generated successfully!");
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (email) {
      toast.success("Successfully subscribed to newsletter!");
      setEmail("");
    }
  };

  const handleWeightChange = (metric, value) => {
    const numValue = parseFloat(value) || 0;
    setTempWeights(prev => ({
      ...prev,
      [metric]: Math.max(0, Math.min(100, numValue)) // Clamp between 0-100
    }));
  };

  const getTotalWeight = () => {
    return Object.values(tempWeights).reduce((sum, w) => sum + w, 0);
  };

  const handleApplyWeights = async () => {
    // Save weights
    setWeights(tempWeights);
    localStorage.setItem('amcWeights', JSON.stringify(tempWeights));
    analytics.trackWeightUpdate(tempWeights);
    analytics.trackWeightModalClose();
    setShowWeightModal(false);
    
    // Fetch fresh data with new weights (pass tempWeights directly to avoid state delay)
    toast.info("Recalculating ratings with new weights...");
    await fetchData(null, null, tempWeights);
    
    // Automatically sort by rating (highest to lowest)
    setSortConfig({ key: 'rating', direction: 'desc' });
    toast.success("Rating weights updated successfully!");
  };

  const handleResetWeights = () => {
    setTempWeights(DEFAULT_WEIGHTS);
    toast.info("Weights reset to default (12.5 each)");
  };

  // Generate mock scheme details
  // Fetch real scheme details from backend
  const fetchSchemeDetails = async (amcName, schemeName) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/scheme-details/${encodeURIComponent(amcName)}/${encodeURIComponent(schemeName)}`
      );
      const data = await response.json();
      if (data.status === 'success' && data.details) {
        return data.details;
      }
      return null;
    } catch (error) {
      console.error('Error fetching scheme details:', error);
      return null;
    }
  };

  // Load scheme details when compare modal opens
  useEffect(() => {
    if (showCompareModal) {
      const comparedSchemes = getComparedSchemes();
      const loadSchemeDetails = async () => {
        const details = {};
        for (const scheme of comparedSchemes) {
          const key = `${scheme.amc_name}_${scheme.scheme_name}`;
          if (!enrichedSchemes[key]) {
            const schemeDetails = await fetchSchemeDetails(scheme.amc_name, scheme.scheme_name);
            if (schemeDetails) {
              details[key] = schemeDetails;
            }
          }
        }
        if (Object.keys(details).length > 0) {
          setEnrichedSchemes(prev => ({ ...prev, ...details }));
        }
      };
      loadSchemeDetails();
    }
  }, [showCompareModal]);

  // Load scheme details when single scheme modal opens
  useEffect(() => {
    if (showSchemeModal && selectedScheme) {
      const loadSchemeDetails = async () => {
        const key = `${selectedScheme.amc_name}_${selectedScheme.scheme_name}`;
        if (!enrichedSchemes[key]) {
          const schemeDetails = await fetchSchemeDetails(selectedScheme.amc_name, selectedScheme.scheme_name);
          if (schemeDetails) {
            setEnrichedSchemes(prev => ({ ...prev, [key]: schemeDetails }));
          }
        }
      };
      loadSchemeDetails();
    }
  }, [showSchemeModal, selectedScheme]);

  const handleSchemeClick = (fund) => {
    analytics.trackSchemeClick(fund.scheme_name, fund.amc_name);
    analytics.trackSchemeModalOpen(fund.scheme_name);
    setSelectedScheme(fund);
    setShowSchemeModal(true);
  };

  const handleContactClick = async () => {
    // Check if contact emails are available first
    try {
      const availabilityResponse = await fetch(
        `${BACKEND_URL}/api/check-contact-availability/${encodeURIComponent(selectedScheme.amc_name)}/${encodeURIComponent(selectedScheme.scheme_name)}`
      );
      const availabilityData = await availabilityResponse.json();
      
      if (!availabilityData.available || availabilityData.count === 0) {
        toast.error("Contact information not available for this AMC. Please visit their website directly.");
        return;
      }
      
      // Fetch contact information for this PM
      const response = await fetch(`${BACKEND_URL}/api/scheme-contacts/${encodeURIComponent(selectedScheme.amc_name)}`);
      const data = await response.json();
      if (data.status === 'success') {
        setSchemeContacts(data.contacts);
        
        // Initialize form with default values
        const greeting = formatGreeting(data.contacts);
        setContactForm(prev => ({
          ...prev,
          subject: `Query to Invest in ${selectedScheme.scheme_name}`,
          message: `${greeting}

I want to get more information on ${selectedScheme.scheme_name}. Do provide me with the latest factsheets, Presentations, fee structures and any other information that you might think is useful.

Best,
${prev.name || '[Your Name]'}`
        }));
      }
      
      analytics.trackContactModalOpen(selectedScheme.scheme_name);
      setShowContactModal(true);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error("Unable to load contact information. Please try again.");
    }
  };

  // Helper to extract title from name
  const getTitle = (name) => {
    if (name.startsWith('Ms.') || name.startsWith('Ms ')) return 'Ms';
    if (name.startsWith('Mrs.') || name.startsWith('Mrs ')) return 'Mrs';
    if (name.startsWith('Mr.') || name.startsWith('Mr ')) return 'Mr';
    return 'Mr'; // Default to Mr if no title found
  };

  // Helper to get first name from full name
  const getFirstName = (fullName) => {
    const parts = fullName.replace(/^(Mr\.|Ms\.|Mrs\.)\s*/i, '').trim().split(' ');
    return parts[0];
  };

  // Format greeting for multiple contacts
  const formatGreeting = (contacts) => {
    if (!contacts) return "Dear Sir/Madam,";
    
    const principal = contacts.principal_officer || {};
    const compliance = contacts.compliance_officer || {};
    
    // Check if we have valid contact names
    if (!principal.name && !compliance.name) {
      return "Dear Sir/Madam,";
    }
    
    // If only one contact available
    if (principal.name && !compliance.name) {
      const title = getTitle(principal.name);
      const firstName = getFirstName(principal.name);
      return `Dear ${title}. ${firstName},`;
    }
    
    if (compliance.name && !principal.name) {
      const title = getTitle(compliance.name);
      const firstName = getFirstName(compliance.name);
      return `Dear ${title}. ${firstName},`;
    }
    
    // Both contacts available
    const principalTitle = getTitle(principal.name);
    const complianceTitle = getTitle(compliance.name);
    const principalFirstName = getFirstName(principal.name);
    const complianceFirstName = getFirstName(compliance.name);
    
    return `Dear ${principalTitle}. ${principalFirstName} & ${complianceTitle}. ${complianceFirstName},`;
  };

  // Get email addresses for "To" field
  const getEmailAddresses = (contacts) => {
    if (!contacts) return [];
    
    const principal = contacts.principal_officer || {};
    const compliance = contacts.compliance_officer || {};
    
    const emails = [];
    
    // Add principal email if exists
    if (principal.email) {
      emails.push(principal.email);
    }
    
    // Add compliance email if exists and different from principal
    if (compliance.email && compliance.email !== principal.email) {
      emails.push(compliance.email);
    }
    
    return emails;
  };

  // Memoize contact modal computed values to prevent recalculation on every render
  const contactModalData = useMemo(() => {
    if (!schemeContacts) return null;
    
    return {
      emailAddresses: getEmailAddresses(schemeContacts),
      greeting: formatGreeting(schemeContacts)
    };
  }, [schemeContacts]);

  const handleSendContact = async () => {
    if (!contactForm.name || !contactForm.email || !contactForm.mobile) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    // Validate message is not empty
    if (!contactForm.message || !contactForm.message.trim()) {
      toast.error("Please enter a message");
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(contactForm.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    // Mobile validation (basic)
    if (contactForm.mobile.length < 10) {
      toast.error("Please enter a valid mobile number");
      return;
    }
    
    setSendingContact(true);
    try {
      const formattedSchemeName = formatSchemeName(selectedScheme.scheme_name);
      const response = await fetch(`${BACKEND_URL}/api/send-contact-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_name: contactForm.name,
          user_email: contactForm.email,
          user_mobile: contactForm.mobile,
          subject: contactForm.subject || `Query to Invest in ${formattedSchemeName}`,
          message: contactForm.message,
          scheme_name: selectedScheme.scheme_name,
          amc_name: selectedScheme.amc_name
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send email');
      }

      const data = await response.json();
      toast.success("Your message has been sent successfully!");
      setShowContactModal(false);
      setContactForm({ name: '', email: '', mobile: '', subject: '', message: '' });
      setSchemeContacts(null);
    } catch (error) {
      console.error('Error sending contact email:', error);
      toast.error(error.message || "Failed to send message. Please try again.");
    } finally {
      setSendingContact(false);
    }
  };


  const handleTestEmail = async () => {
    if (!contactForm.name || !contactForm.email || !contactForm.mobile) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    // Validate message is not empty
    if (!contactForm.message || !contactForm.message.trim()) {
      toast.error("Please enter a message");
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(contactForm.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    // Mobile validation (basic)
    if (contactForm.mobile.length < 10) {
      toast.error("Please enter a valid mobile number");
      return;
    }
    
    setSendingContact(true);
    try {
      const formattedSchemeName = formatSchemeName(selectedScheme.scheme_name);
      const response = await fetch(`${BACKEND_URL}/api/send-contact-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_name: contactForm.name,
          user_email: contactForm.email,
          user_mobile: contactForm.mobile,
          subject: contactForm.subject || `Query to Invest in ${formattedSchemeName}`,
          message: contactForm.message,
          scheme_name: selectedScheme.scheme_name,
          amc_name: selectedScheme.amc_name,
          test_mode: true,  // Flag to send to test email
          test_email: "reach@onetreehill.in"
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send test email');
      }

      const data = await response.json();
      toast.success("Test email sent to reach@onetreehill.in!");
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error(error.message || "Failed to send test email. Please try again.");
    } finally {
      setSendingContact(false);
    }
  };

  const comparedCount = Object.values(comparedItems).filter(Boolean).length;

  // Scroll to search section with offset for header
  const scrollToSearch = () => {
    analytics.trackGetStartedClick();
    const searchSection = document.querySelector('.filters-section');
    if (searchSection) {
      const headerOffset = 100; // Offset to account for header height
      const elementPosition = searchSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation Bar */}
      <nav className="nav-bar">
        <div className="nav-container">
          <div className="nav-logo">
            <img src="/pmskart-logo.png" alt="PMS Kart Logo" className="h-24 w-auto" />
          </div>
          
          {/* Desktop Menu */}
          <div className="nav-menu hidden md:flex">
            <button onClick={() => setShowFeeCalculatorModal(true)} className="nav-link bg-transparent border-0 cursor-pointer">Fee Calculator</button>
            <button onClick={() => setShowGeneralContactModal(true)} className="nav-link bg-transparent border-0 cursor-pointer">Contact</button>
            
            {/* Login Dropdown */}
            <div className="relative">
              <Button 
                variant="outline" 
                className="login-btn flex items-center gap-2"
                onClick={() => setShowLoginDropdown(!showLoginDropdown)}
              >
                <LogIn className="w-4 h-4" />
                Login
                <ChevronDown className="w-4 h-4" />
              </Button>
              
              {showLoginDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white border border-slate-200 rounded-lg shadow-lg z-50">
                  <button
                    onClick={() => {
                      setShowLoginDropdown(false);
                      navigate('/login?type=admin');
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 text-slate-700 text-sm font-medium border-b border-slate-100"
                  >
                    Admin Login
                  </button>
                  <button
                    onClick={() => {
                      setShowLoginDropdown(false);
                      // Go directly to AMC dashboard without login
                      navigate('/amc-dashboard');
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 text-slate-700 text-sm font-medium"
                  >
                    AMC Login
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2 text-slate-700 hover:bg-slate-100 rounded-lg"
            onClick={() => setShowMobileMenu(!showMobileMenu)}
          >
            {showMobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {showMobileMenu && (
          <div className="md:hidden border-t border-slate-200 bg-white">
            <div className="nav-container flex-col items-stretch py-4 space-y-2">
              <button 
                onClick={() => {
                  setShowFeeCalculatorModal(true);
                  setShowMobileMenu(false);
                }} 
                className="nav-link bg-transparent border-0 cursor-pointer text-left px-4 py-3 hover:bg-slate-50 rounded-lg"
              >
                <Calculator className="w-4 h-4 inline mr-2" />
                Fee Calculator
              </button>
              <button 
                onClick={() => {
                  setShowGeneralContactModal(true);
                  setShowMobileMenu(false);
                }} 
                className="nav-link bg-transparent border-0 cursor-pointer text-left px-4 py-3 hover:bg-slate-50 rounded-lg"
              >
                <Mail className="w-4 h-4 inline mr-2" />
                Contact
              </button>
              <button
                onClick={() => {
                  setShowMobileMenu(false);
                  navigate('/login?type=admin');
                }}
                className="nav-link bg-transparent border-0 cursor-pointer text-left px-4 py-3 hover:bg-slate-50 rounded-lg"
              >
                <User className="w-4 h-4 inline mr-2" />
                Admin Login
              </button>
              <button
                onClick={() => {
                  setShowMobileMenu(false);
                  navigate('/amc-dashboard');
                }}
                className="nav-link bg-transparent border-0 cursor-pointer text-left px-4 py-3 hover:bg-slate-50 rounded-lg"
              >
                <LogIn className="w-4 h-4 inline mr-2" />
                AMC Login
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="hero-banner">
        <div className="hero-text-container">
          <div className="hero-text">
            <h1 className="hero-title">
              Discover India's Top
              <span className="hero-title-highlight"> Portfolio Managers</span>
            </h1>
            <div className="hero-features">
              <p className="hero-feature-item">Zero Sign-up Drama</p>
              <p className="hero-feature-item">Works Instantly</p>
              <p className="hero-feature-item">Customized Ranking</p>
              <p className="hero-feature-item">Clear Comparisons</p>
              <p className="hero-feature-item">No Subscriptions</p>
            </div>
            <div className="flex gap-4 flex-wrap justify-center">
              <button onClick={scrollToSearch} className="hero-button">
                Get Started
              </button>
              <button 
                onClick={() => setShowCalendlyModal(true)} 
                className="hero-button-secondary"
              >
                Schedule a Meeting
              </button>
            </div>
          </div>
        </div>
        <div className="hero-image-container">
          <img 
            src="https://customer-assets.emergentagent.com/job_amc-analyzer/artifacts/s3d7fi93_Screenshot_2025-11-19_at_1.46.18_PM-removebg-preview.png" 
            alt="Portfolio Management Illustration" 
            className="hero-img"
          />
        </div>
      </section>

      {/* Header */}
      <header className="header"></header>

      {/* Comparison Modal */}
      {showCompareModal && (() => {
        const comparedSchemes = getComparedSchemes();
        
        // Prepare data for grouped bar chart
        const periods = ['1M', '3M', '6M', '1Y', '2Y', '3Y', '5Y', 'SI'];
        const periodKeys = ['1_month', '3_month', '6_month', '1_year', '2_year', '3_year', '5_year', 'since_inception'];
        const chartData = periods.map((period, idx) => {
          const dataPoint = { period };
          comparedSchemes.forEach(scheme => {
            dataPoint[scheme.scheme_name] = parseFloat(scheme[periodKeys[idx]]) || 0;
          });
          return dataPoint;
        });
        
        // Colors for different schemes - vibrant color palette
        const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7B731', '#5F27CD', '#00D2D3'];
        
        // Helper function to get enriched scheme data
        const getSchemeDetails = (scheme) => {
          const key = `${scheme.amc_name}_${scheme.scheme_name}`;
          return enrichedSchemes[key] || {};
        };
        
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[95vh] md:max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-gradient-to-r from-teal-600 to-blue-600 text-white px-3 py-3 md:px-6 md:py-4 flex items-center justify-between z-10">
                <div>
                  <h2 className="text-base md:text-2xl font-bold">Scheme Comparison</h2>
                  <p className="text-teal-100 text-xs md:text-sm mt-1">Comparing {comparedSchemes.length} schemes</p>
                </div>
                <button 
                  onClick={() => {
                    analytics.trackCompareModalClose();
                    setShowCompareModal(false);
                  }}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 md:w-6 md:h-6" />
                </button>
              </div>
              
              <div className="p-3 md:p-6 space-y-4 md:space-y-6">
                {/* Performance Comparison Bar Chart */}
                <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-lg p-3 md:p-6 border border-blue-200">
                  <h3 className="text-sm md:text-xl font-semibold text-slate-800 mb-3 md:mb-4 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-teal-600" />
                    <span className="text-xs md:text-base">Performance Comparison</span>
                  </h3>
                  <ResponsiveContainer width="100%" height={window.innerWidth < 768 ? 350 : 400}>
                    <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: window.innerWidth < 768 ? 60 : 80 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis 
                        dataKey="period" 
                        tick={{ fill: '#475569', fontSize: window.innerWidth < 768 ? 9 : 12 }}
                      />
                      <YAxis 
                        tick={{ fill: '#475569', fontSize: window.innerWidth < 768 ? 9 : 12 }}
                        label={{ value: 'Returns (%)', angle: -90, position: 'insideLeft', style: { fill: '#475569', fontSize: window.innerWidth < 768 ? 10 : 12 } }}
                        width={window.innerWidth < 768 ? 40 : 60}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                          fontSize: window.innerWidth < 768 ? '10px' : '12px'
                        }}
                        formatter={(value) => [value === 0 ? 'N/A' : `${value.toFixed(2)}%`, '']}
                      />
                      <Legend 
                        wrapperStyle={{ paddingTop: '10px', fontSize: window.innerWidth < 768 ? '10px' : '12px' }}
                        iconType="circle"
                      />
                      {comparedSchemes.map((scheme, idx) => (
                        <Bar 
                          key={scheme.scheme_name}
                          dataKey={scheme.scheme_name} 
                          fill={colors[idx % colors.length]}
                          radius={[4, 4, 0, 0]}
                          name={window.innerWidth < 768 && scheme.scheme_name.length > 20 ? scheme.scheme_name.substring(0, 20) + '...' : scheme.scheme_name.length > 30 ? scheme.scheme_name.substring(0, 30) + '...' : scheme.scheme_name}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Performance Metrics Table */}
                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-slate-100 to-slate-50 px-3 md:px-4 py-2 md:py-3 border-b border-slate-200">
                    <h3 className="text-sm md:text-lg font-semibold text-slate-800">Performance Metrics (%)</h3>
                  </div>
                  
                  {/* Desktop view - schemes as rows, periods as columns */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 sticky left-0 bg-slate-50">Scheme</th>
                          {periods.map((period) => (
                            <th key={period} className="px-4 py-3 text-center text-sm font-semibold text-slate-700 whitespace-nowrap">
                              {period}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {comparedSchemes.map((scheme, idx) => (
                          <tr key={scheme.scheme_name} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="px-4 py-3 text-sm sticky left-0 bg-white max-w-xs">
                              <div className="font-semibold text-slate-900">{scheme.amc_name}</div>
                              <div className="text-xs text-slate-600 mt-0.5">{scheme.scheme_name}</div>
                            </td>
                            {periodKeys.map((key) => (
                              <td 
                                key={key}
                                className={`px-4 py-3 text-center text-sm font-semibold whitespace-nowrap ${
                                  parseFloat(scheme[key]) > 0 ? 'text-green-600' : parseFloat(scheme[key]) < 0 ? 'text-red-600' : ''
                                }`}
                              >
                                {formatPerformanceValue(scheme[key], 2)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Mobile view - transposed: periods as rows, schemes as columns */}
                  <div className="md:hidden overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-3 py-2 text-center text-xs font-semibold text-slate-700 sticky left-0 bg-slate-50 w-16">Period</th>
                          {comparedSchemes.map((scheme) => (
                            <th key={scheme.scheme_name} className="px-2 py-2 text-center text-[10px] font-semibold text-slate-700">
                              <div className="max-w-[80px] truncate mx-auto">{scheme.amc_name}</div>
                              <div className="text-[9px] font-normal text-slate-600 mt-0.5 max-w-[80px] truncate mx-auto">{scheme.scheme_name}</div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {periods.map((period, pIdx) => (
                          <tr key={period} className={`border-b border-slate-100 ${pIdx % 2 === 0 ? 'bg-slate-50' : 'bg-white'}`}>
                            <td className="px-3 py-2 text-xs font-semibold text-slate-700 sticky left-0 bg-inherit">
                              {period}
                            </td>
                            {comparedSchemes.map((scheme) => (
                              <td 
                                key={scheme.scheme_name}
                                className={`px-2 py-2 text-center text-[10px] font-semibold ${
                                  parseFloat(scheme[periodKeys[pIdx]]) > 0 ? 'text-green-600' : 
                                  parseFloat(scheme[periodKeys[pIdx]]) < 0 ? 'text-red-600' : 'text-slate-600'
                                }`}
                              >
                                {formatPerformanceValue(scheme[periodKeys[pIdx]], 2)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Scheme Details Table */}
                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-purple-100 to-pink-50 px-4 py-3 border-b border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-800">Scheme Details</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700 border-r border-slate-200 sticky left-0 bg-slate-50 w-48">
                            Scheme Name
                          </th>
                          {comparedSchemes.map((scheme) => (
                            <th key={scheme.scheme_name} className="px-4 py-3 text-center text-sm font-semibold text-slate-700">
                              <div className="font-medium text-slate-900">{scheme.amc_name}</div>
                              <div className="text-xs font-normal text-slate-600 mt-1">{scheme.scheme_name}</div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Fund Manager Name(s)
                          </td>
                          {comparedSchemes.map((scheme) => {
                            const details = getSchemeDetails(scheme);
                            return (
                              <td key={scheme.scheme_name} className="px-4 py-3 text-sm text-slate-800 text-center">
                                {details.fund_manager || '-'}
                              </td>
                            );
                          })}
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Fund Manager Bio
                          </td>
                          {comparedSchemes.map((scheme) => {
                            const details = getSchemeDetails(scheme);
                            return (
                              <td key={scheme.scheme_name} className="px-4 py-3 text-sm text-slate-800 text-center">
                                {details.fund_manager_bio || '-'}
                              </td>
                            );
                          })}
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Inception Date
                          </td>
                          {comparedSchemes.map((scheme) => {
                            const details = getSchemeDetails(scheme);
                            return (
                              <td key={scheme.scheme_name} className="px-4 py-3 text-sm text-slate-800 text-center">
                                {details.inception_date || '-'}
                              </td>
                            );
                          })}
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Edge in Investing
                          </td>
                          {comparedSchemes.map((scheme) => {
                            const details = getSchemeDetails(scheme);
                            return (
                              <td key={scheme.scheme_name} className="px-4 py-3 text-sm text-slate-800 text-center">
                                {details.edge_in_investing || '-'}
                              </td>
                            );
                          })}
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            AUM (Crores)
                          </td>
                          {comparedSchemes.map((scheme) => (
                            <td key={scheme.scheme_name} className="px-4 py-3 text-sm text-slate-800 font-semibold text-center">
                              ₹{Math.round(parseFloat(scheme.aum) || 0).toLocaleString('en-IN')} Cr
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Fee Structure Comparison Table */}
                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-green-100 to-emerald-50 px-4 py-3 border-b border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-800">Fee Structure Comparison</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 border-r border-slate-200">
                            Scheme
                          </th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700 w-32">Structure 1</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700 w-32">Structure 2</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700 w-32">Structure 3</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comparedSchemes.map((scheme, schemeIdx) => {
                          const details = getSchemeDetails(scheme);
                          return (
                            <React.Fragment key={scheme.scheme_name}>
                              {/* Scheme Name Row */}
                              <tr className="bg-slate-50 border-b border-slate-200">
                                <td colSpan="4" className="px-4 py-2 text-sm font-semibold text-slate-900">
                                  {scheme.amc_name} - {scheme.scheme_name}
                                </td>
                              </tr>
                              {/* Management Fee */}
                              <tr className="border-b border-slate-100 hover:bg-slate-50">
                                <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 pl-8">
                                  Management Fee (%)
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-800 text-center">
                                  {details.fee_structure_1_management ? `${details.fee_structure_1_management}%` : '-'}
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-800 text-center">
                                  {details.fee_structure_2_management ? `${details.fee_structure_2_management}%` : '-'}
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-800 text-center">
                                  {details.fee_structure_3_management ? `${details.fee_structure_3_management}%` : '-'}
                                </td>
                              </tr>
                              {/* Hurdle Rate */}
                              <tr className="border-b border-slate-100 hover:bg-slate-50">
                                <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 pl-8">
                                  Hurdle Rate (%)
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-800 text-center">
                                  {details.fee_structure_1_hurdle ? `${details.fee_structure_1_hurdle}%` : '-'}
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-800 text-center">
                                  {details.fee_structure_2_hurdle ? `${details.fee_structure_2_hurdle}%` : '-'}
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-800 text-center">
                                  {details.fee_structure_3_hurdle ? `${details.fee_structure_3_hurdle}%` : '-'}
                                </td>
                              </tr>
                              {/* Performance Fee */}
                              <tr className="border-b border-slate-100 hover:bg-slate-50">
                                <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 pl-8">
                                  Performance Fee (%)
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-800 text-center">
                                  {details.fee_structure_1_performance ? `${details.fee_structure_1_performance}%` : '-'}
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-800 text-center">
                                  {details.fee_structure_2_performance ? `${details.fee_structure_2_performance}%` : '-'}
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-800 text-center">
                                  {details.fee_structure_3_performance ? `${details.fee_structure_3_performance}%` : '-'}
                                </td>
                              </tr>
                              {/* Exit Load (common for all) */}
                              <tr className="border-b-2 border-slate-300 hover:bg-slate-50">
                                <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 pl-8">
                                  Exit Load (%)
                                </td>
                                <td colSpan="3" className="px-4 py-3 text-sm text-slate-800 text-center font-semibold">
                                  {details.exit_load ? `${details.exit_load}%` : '-'}
                                </td>
                              </tr>
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Other Details Table */}
                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-purple-100 to-pink-50 px-4 py-3 border-b border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-800">Other Details</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700 border-r border-slate-200 sticky left-0 bg-slate-50 w-48">
                            Field
                          </th>
                          {comparedSchemes.map((scheme) => (
                            <th key={scheme.scheme_name} className="px-4 py-3 text-center text-sm font-semibold text-slate-700">
                              <div className="font-medium text-slate-900">{scheme.amc_name}</div>
                              <div className="text-xs font-normal text-slate-600 mt-1">{scheme.scheme_name}</div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Type of Fund
                          </td>
                          {comparedSchemes.map((scheme) => {
                            const details = getSchemeDetails(scheme);
                            return (
                              <td key={scheme.scheme_name} className="px-4 py-3 text-sm text-slate-800 text-center">
                                {details.fund_type || '-'}
                              </td>
                            );
                          })}
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Size of Fund
                          </td>
                          {comparedSchemes.map((scheme) => {
                            const details = getSchemeDetails(scheme);
                            return (
                              <td key={scheme.scheme_name} className="px-4 py-3 text-sm text-slate-800 text-center">
                                {details.fund_size || '-'}
                              </td>
                            );
                          })}
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Rating
                          </td>
                          {comparedSchemes.map((scheme) => (
                            <td key={scheme.scheme_name} className="px-4 py-3">
                              <div className="flex justify-center">
                                <StarRating rating={scheme.rating || 0} />
                              </div>
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Presentation Link
                          </td>
                          {comparedSchemes.map((scheme) => {
                            const details = getSchemeDetails(scheme);
                            return (
                              <td key={scheme.scheme_name} className="px-4 py-3">
                                <div className="flex justify-center">
                                  {details.presentation_link ? (
                                    <a 
                                      href={details.presentation_link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-teal-600 hover:text-teal-700 text-sm flex items-center gap-1 font-medium"
                                    >
                                      View Link
                                      <ExternalLink className="w-4 h-4" />
                                    </a>
                                  ) : (
                                    '-'
                                  )}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Sales Contact Email
                          </td>
                          {comparedSchemes.map((scheme) => {
                            const details = getSchemeDetails(scheme);
                            return (
                              <td key={scheme.scheme_name} className="px-4 py-3 text-sm text-slate-800 text-center">
                                {details.sales_email || '-'}
                              </td>
                            );
                          })}
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Sales Contact Mobile
                          </td>
                          {comparedSchemes.map((scheme) => {
                            const details = getSchemeDetails(scheme);
                            return (
                              <td key={scheme.scheme_name} className="px-4 py-3 text-sm text-slate-800 text-center">
                                {details.sales_mobile || '-'}
                              </td>
                            );
                          })}
                        </tr>
                        <tr className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                            Contact
                          </td>
                          {comparedSchemes.map((scheme) => (
                            <td key={scheme.scheme_name} className="px-4 py-3">
                              <div className="flex justify-center">
                                <Button
                                  onClick={async () => {
                                    // Check if contact emails are available first
                                    try {
                                      const availabilityResponse = await fetch(
                                        `${BACKEND_URL}/api/check-contact-availability/${encodeURIComponent(scheme.amc_name)}/${encodeURIComponent(scheme.scheme_name)}`
                                      );
                                      const availabilityData = await availabilityResponse.json();
                                      
                                      if (!availabilityData.available || availabilityData.count === 0) {
                                        toast.error("Contact information not available for this AMC. Please visit their website directly.");
                                        return;
                                      }
                                      
                                      // Fetch contact info and open contact modal
                                      const response = await fetch(`${BACKEND_URL}/api/scheme-contacts/${encodeURIComponent(scheme.amc_name)}`);
                                      const data = await response.json();
                                      if (data.status === 'success') {
                                        setSchemeContacts(data.contacts);
                                      }
                                      
                                      setSelectedScheme(scheme);
                                      setShowContactModal(true);
                                    } catch (error) {
                                      console.error('Error fetching contacts:', error);
                                      toast.error("Unable to load contact information. Please try again.");
                                    }
                                  }}
                                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-4 py-2 text-sm flex items-center gap-1"
                                >
                                  <Mail className="w-4 h-4" />
                                  Contact
                                </Button>
                              </div>
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Contact Form Modal */}
      {showContactModal && (
        <Suspense fallback={<div>Loading...</div>}>
          <ContactModal 
            isOpen={showContactModal}
            onClose={() => {
              setShowContactModal(false);
              setSchemeContacts(null);
            }}
            selectedScheme={selectedScheme}
            schemeContacts={schemeContacts}
            formatGreeting={formatGreeting}
            toast={toast}
          />
        </Suspense>
      )}

      {/* Scheme Detail Modal */}
      {showSchemeModal && selectedScheme && (() => {
        const key = `${selectedScheme.amc_name}_${selectedScheme.scheme_name}`;
        const details = enrichedSchemes[key] || {};
        
        const performanceData = [
          { period: '1M', value: parseFloat(selectedScheme['1_month']) || 0, label: '1 Month' },
          { period: '3M', value: parseFloat(selectedScheme['3_month']) || 0, label: '3 Months' },
          { period: '6M', value: parseFloat(selectedScheme['6_month']) || 0, label: '6 Months' },
          { period: '1Y', value: parseFloat(selectedScheme['1_year']) || 0, label: '1 Year' },
          { period: '2Y', value: parseFloat(selectedScheme['2_year']) || 0, label: '2 Years' },
          { period: '3Y', value: parseFloat(selectedScheme['3_year']) || 0, label: '3 Years' },
          { period: '5Y', value: parseFloat(selectedScheme['5_year']) || 0, label: '5 Years' },
          { period: 'SI', value: parseFloat(selectedScheme.since_inception) || 0, label: 'Since Inception' }
        ];

        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[95vh] md:max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-gradient-to-r from-teal-600 to-blue-600 text-white px-3 py-3 md:px-6 md:py-4 flex items-center justify-between z-10">
                <div>
                  <h2 className="text-base md:text-2xl font-bold">{selectedScheme.scheme_name}</h2>
                  <p className="text-teal-100 text-xs md:text-sm mt-1">{selectedScheme.amc_name}</p>
                </div>
                <button 
                  onClick={() => {
                    analytics.trackSchemeModalClose();
                    setShowSchemeModal(false);
                  }}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 md:w-6 md:h-6" />
                </button>
              </div>
              
              <div className="p-3 md:p-6 space-y-4 md:space-y-6">
                {/* Performance Bar Chart */}
                <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-lg p-3 md:p-6 border border-blue-200">
                  <h3 className="text-sm md:text-xl font-semibold text-slate-800 mb-3 md:mb-4 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-teal-600" />
                    <span className="text-xs md:text-base">Performance Overview</span>
                  </h3>
                  <ResponsiveContainer width="100%" height={window.innerWidth < 768 ? 350 : 300}>
                    <BarChart data={performanceData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis 
                        dataKey="label" 
                        tick={{ fill: '#475569', fontSize: window.innerWidth < 768 ? 9 : 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={window.innerWidth < 768 ? 60 : 80}
                      />
                      <YAxis 
                        tick={{ fill: '#475569', fontSize: window.innerWidth < 768 ? 9 : 12 }}
                        label={{ value: 'Returns (%)', angle: -90, position: 'insideLeft', style: { fill: '#475569', fontSize: window.innerWidth < 768 ? 10 : 12 } }}
                        width={window.innerWidth < 768 ? 40 : 60}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                          fontSize: window.innerWidth < 768 ? '10px' : '12px'
                        }}
                        formatter={(value) => [value === 0 ? 'N/A' : `${value.toFixed(2)}%`, 'Returns']}
                      />
                      <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                        {performanceData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.value >= 0 ? '#0d9488' : '#ef4444'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Performance Metrics Table */}
                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-slate-100 to-slate-50 px-3 md:px-4 py-2 md:py-3 border-b border-slate-200">
                    <h3 className="text-sm md:text-lg font-semibold text-slate-800">Performance Metrics</h3>
                  </div>
                  
                  {/* Desktop view - horizontal table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          {performanceData.map((item) => (
                            <th key={item.period} className="px-4 py-3 text-center text-sm font-semibold text-slate-700">
                              {item.period}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-slate-100">
                          {performanceData.map((item) => (
                            <td 
                              key={item.period} 
                              className={`px-4 py-3 text-center font-semibold ${
                                item.value > 0 ? 'text-green-600' : item.value < 0 ? 'text-red-600' : ''
                              }`}
                            >
                              {formatPerformanceValue(item.value, 2)}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Mobile view - transposed table (rows become columns) */}
                  <div className="md:hidden">
                    <table className="w-full">
                      <tbody>
                        {performanceData.map((item, idx) => (
                          <tr key={item.period} className={`border-b border-slate-100 ${idx % 2 === 0 ? 'bg-slate-50' : 'bg-white'}`}>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 w-20">
                              {item.period}
                            </th>
                            <td 
                              className={`px-3 py-2 text-right text-sm font-semibold ${
                                item.value > 0 ? 'text-green-600' : item.value < 0 ? 'text-red-600' : 'text-slate-600'
                              }`}
                            >
                              {formatPerformanceValue(item.value, 2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Scheme Details Table (Transposed) */}
                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-purple-100 to-pink-50 px-4 py-3 border-b border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-800">Scheme Details</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 border-r border-slate-200 w-48">
                            Metric
                          </th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">
                            {selectedScheme.scheme_name}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200">
                            Fund Manager Name(s)
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-800 text-center">
                            {details.fund_manager || '-'}
                          </td>
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200">
                            Fund Manager Bio
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-800 text-center">
                            {details.fund_manager_bio || '-'}
                          </td>
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200">
                            Inception Date
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-800 text-center">
                            {details.inception_date || '-'}
                          </td>
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200">
                            Edge in Investing
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-800 text-center">
                            {details.edge_in_investing || '-'}
                          </td>
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200">
                            AUM (Crores)
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-800 text-center font-semibold">
                            ₹{Math.round(parseFloat(selectedScheme.aum) || 0).toLocaleString('en-IN')} Cr
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Fee Structure Comparison Table */}
                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-green-100 to-emerald-50 px-4 py-3 border-b border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-800">Fee Structure Comparison</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 border-r border-slate-200 w-48">
                            Component
                          </th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700 w-32">Structure 1</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700 w-32">Structure 2</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700 w-32">Structure 3</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Management Fee */}
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200">
                            Management Fee (%)
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-800 text-center">
                            {details.fee_structure_1_management ? `${details.fee_structure_1_management}%` : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-800 text-center">
                            {details.fee_structure_2_management ? `${details.fee_structure_2_management}%` : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-800 text-center">
                            {details.fee_structure_3_management ? `${details.fee_structure_3_management}%` : '-'}
                          </td>
                        </tr>
                        {/* Hurdle Rate */}
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200">
                            Hurdle Rate (%)
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-800 text-center">
                            {details.fee_structure_1_hurdle ? `${details.fee_structure_1_hurdle}%` : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-800 text-center">
                            {details.fee_structure_2_hurdle ? `${details.fee_structure_2_hurdle}%` : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-800 text-center">
                            {details.fee_structure_3_hurdle ? `${details.fee_structure_3_hurdle}%` : '-'}
                          </td>
                        </tr>
                        {/* Performance Fee */}
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200">
                            Performance Fee (%)
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-800 text-center">
                            {details.fee_structure_1_performance ? `${details.fee_structure_1_performance}%` : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-800 text-center">
                            {details.fee_structure_2_performance ? `${details.fee_structure_2_performance}%` : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-800 text-center">
                            {details.fee_structure_3_performance ? `${details.fee_structure_3_performance}%` : '-'}
                          </td>
                        </tr>
                        {/* Exit Load (common for all) */}
                        <tr className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200">
                            Exit Load (%)
                          </td>
                          <td colSpan="3" className="px-4 py-3 text-sm text-slate-800 text-center font-semibold">
                            {details.exit_load ? `${details.exit_load}%` : '-'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Other Details Table */}
                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-purple-100 to-pink-50 px-4 py-3 border-b border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-800">Other Details</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <tbody>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200">
                            Type of Fund
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-800">
                            {details.fund_type || '-'}
                          </td>
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200">
                            Size of Fund
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-800">
                            {details.fund_size || '-'}
                          </td>
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200">
                            Rating
                          </td>
                          <td className="px-4 py-3">
                            <StarRating rating={selectedScheme.rating} />
                          </td>
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200">
                            Presentation Link
                          </td>
                          <td className="px-4 py-3">
                            {details.presentation_link ? (
                              <a 
                                href={details.presentation_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-teal-600 hover:text-teal-700 text-sm flex items-center gap-1 font-medium"
                              >
                                View Link
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            ) : (
                              '-'
                            )}
                          </td>
                        </tr>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200">
                            Sales Contact Email
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-800">
                            {details.sales_email || '-'}
                          </td>
                        </tr>
                        <tr className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200">
                            Sales Contact Mobile
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-800">
                            {details.sales_mobile || '-'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Contact Button */}
                <div className="flex justify-center pt-4">
                  <Button
                    onClick={handleContactClick}
                    className="bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 text-white px-8 py-3 text-lg font-semibold flex items-center gap-2 shadow-lg"
                  >
                    <Mail className="w-5 h-5" />
                    Contact Us
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Weight Configuration Modal */}
      {showWeightModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Settings className="w-6 h-6 text-teal-600" />
                <h2 className="text-xl font-semibold text-slate-800">Configure Rating Weights</h2>
              </div>
              <button 
                onClick={() => setShowWeightModal(false)}
                className="p-1 hover:bg-slate-100 rounded transition-colors"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            <div className="p-6">
              <p className="text-sm text-slate-600 mb-6">
                Customize how each performance metric contributes to the overall star rating. 
                Values can range from 0 to 100. If the total exceeds 100, weights will be automatically normalized.
              </p>

              <div className="grid grid-cols-2 gap-4 mb-6">
                {[
                  { key: '1_month', label: '1 Month' },
                  { key: '3_month', label: '3 Months' },
                  { key: '6_month', label: '6 Months' },
                  { key: '1_year', label: '1 Year' },
                  { key: '2_year', label: '2 Years' },
                  { key: '3_year', label: '3 Years' },
                  { key: '5_year', label: '5 Years' },
                  { key: 'since_inception', label: 'Since Inception' }
                ].map(({ key, label }) => (
                  <div key={key} className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">{label}</label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={tempWeights[key]}
                      onChange={(e) => handleWeightChange(key, e.target.value)}
                      className="w-full"
                    />
                  </div>
                ))}
              </div>

              <div className={`p-4 rounded-lg border-2 mb-6 ${
                getTotalWeight() > 100 
                  ? 'bg-orange-50 border-orange-300' 
                  : getTotalWeight() === 100 
                  ? 'bg-green-50 border-green-300'
                  : 'bg-blue-50 border-blue-300'
              }`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-700">Total Weight:</span>
                  <span className={`text-2xl font-bold ${
                    getTotalWeight() > 100 
                      ? 'text-orange-600' 
                      : getTotalWeight() === 100 
                      ? 'text-green-600'
                      : 'text-blue-600'
                  }`}>
                    {getTotalWeight().toFixed(1)}
                  </span>
                </div>
                {getTotalWeight() > 100 && (
                  <p className="text-xs text-orange-700 mt-2">
                    ⚠️ Total exceeds 100. Weights will be normalized automatically.
                  </p>
                )}
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleResetWeights}
                  variant="outline"
                  className="flex-1"
                >
                  Reset to Default
                </Button>
                <Button
                  onClick={handleApplyWeights}
                  className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
                >
                  Apply Weights
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="main-content">
        {/* Filters Section */}
        <div className="filters-section">
          {/* Row 1: Search - Full width on mobile */}
          <div className="filter-item search-filter w-full md:w-auto md:flex-1 md:max-w-[400px]">
            <div className="search-wrapper">
              <Search className="search-icon" />
              <Input 
                type="text" 
                placeholder="Search AMC or Scheme..." 
                className="search-input"
                value={searchQuery}
                onChange={handleSearch}
              />
            </div>
          </div>

          {/* Row 2 on mobile: Scheme Type + Sort By (mobile only) */}
          <div className="grid grid-cols-2 gap-2 w-full md:flex md:w-auto">
            {/* Scheme Type Filter */}
            <Select value={schemeFilter} onValueChange={setSchemeFilter}>
              <SelectTrigger className="w-full md:w-[180px] h-[40px] border-teal-600 text-teal-700 hover:bg-teal-50 text-sm">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Schemes</SelectItem>
                <SelectItem value="pms">PMS Only</SelectItem>
                <SelectItem value="aif">AIF Only</SelectItem>
              </SelectContent>
            </Select>

            {/* Sort By - Mobile Only */}
            <div className="md:hidden">
              <Select 
                value={sortConfig.key ? `${sortConfig.key}-${sortConfig.direction}` : 'rating-desc'} 
                onValueChange={(value) => {
                  const [key, direction] = value.split('-');
                  setSortConfig({ key, direction });
                }}
              >
                <SelectTrigger className="w-full h-[40px] border-blue-600 text-blue-700 hover:bg-blue-50 text-sm">
                  <SelectValue placeholder="Sort" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rating-desc">Rating ↓</SelectItem>
                  <SelectItem value="rating-asc">Rating ↑</SelectItem>
                  <SelectItem value="aum-desc">AUM ↓</SelectItem>
                  <SelectItem value="aum-asc">AUM ↑</SelectItem>
                  <SelectItem value="1_year-desc">1Y Return ↓</SelectItem>
                  <SelectItem value="1_year-asc">1Y Return ↑</SelectItem>
                  <SelectItem value="3_year-desc">3Y Return ↓</SelectItem>
                  <SelectItem value="3_year-asc">3Y Return ↑</SelectItem>
                  <SelectItem value="5_year-desc">5Y Return ↓</SelectItem>
                  <SelectItem value="5_year-asc">5Y Return ↑</SelectItem>
                  <SelectItem value="amc_name-asc">AMC A-Z</SelectItem>
                  <SelectItem value="amc_name-desc">AMC Z-A</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row 3 on mobile: AUM Range */}
          <div className="flex items-center gap-2 border border-slate-300 rounded-md px-3 h-[40px] bg-white w-full md:w-auto">
            <span className="text-sm text-slate-600 font-medium whitespace-nowrap">AUM (Cr):</span>
            <Input
              type="number"
              value={minAum}
              onChange={(e) => setMinAum(Number(e.target.value))}
              placeholder="Min"
              className="w-[70px] md:w-[95px] h-[28px] text-sm border-slate-300"
            />
            <span className="text-slate-400">-</span>
            <Input
              type="number"
              value={maxAum}
              onChange={(e) => setMaxAum(Number(e.target.value))}
              placeholder="Max"
              className="w-[70px] md:w-[95px] h-[28px] text-sm border-slate-300"
            />
          </div>

          {/* Row 4 on mobile: Configure Rating Weights */}
          <Button
            onClick={() => {
              analytics.trackWeightModalOpen();
              setShowWeightModal(true);
            }}
            variant="outline"
            className="flex items-center justify-center gap-2 border-purple-600 text-purple-600 hover:bg-purple-50 h-[40px] px-4 w-full md:w-auto"
          >
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">Configure Rating Weights</span>
            <span className="sm:hidden">Rating Weights</span>
          </Button>

          {/* Compare Badge */}
          {comparedCount > 0 && (
            <button 
              onClick={handleOpenCompare}
              className="compare-badge w-full md:w-auto"
            >
              Click to compare {comparedCount} {comparedCount === 1 ? 'fund' : 'funds'}
            </button>
          )}
          
          {loading && (
            <div className="flex items-center gap-3 text-sm text-teal-600 bg-teal-50 px-4 py-2 rounded-lg border border-teal-200">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="font-medium">Loading performance data...</span>
            </div>
          )}

          {autoRefreshEnabled && !loading && (
            <div className="flex items-center gap-2 text-xs text-green-600 bg-green-50 px-3 py-1.5 rounded-full">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              Auto-refresh enabled (60s)
            </div>
          )}
        </div>

        {/* Data Table */}
        <div className="table-container relative">
          {loading && (
            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
              <div className="flex flex-col items-center gap-4 p-8">
                <Loader2 className="w-12 h-12 animate-spin text-teal-600" />
                <div className="text-center">
                  <p className="text-lg font-semibold text-slate-800">Loading Performance Data</p>
                  <p className="text-sm text-slate-600 mt-1">Please wait while we fetch the latest data...</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Mobile Card View */}
          <div className="block md:hidden space-y-4">
            {paginatedData.map((fund, idx) => (
              <div 
                key={`${fund.amc_name}-${fund.scheme_name}-${idx}`}
                className="bg-white rounded-lg shadow-md border border-slate-200 overflow-hidden"
                style={fund.scheme_category === 'AIF' ? { borderLeft: '4px solid #6366f1' } : {}}
              >
                {/* Card Header */}
                <div className="p-4 bg-slate-50 border-b border-slate-200">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="font-semibold text-slate-800 text-sm">{fund.amc_name}</div>
                    </div>
                    {fund.scheme_category === 'AIF' && (
                      <div style={{
                        border: '1.5px solid #6366f1',
                        borderRadius: '8px',
                        padding: '2px 6px',
                        fontSize: '10px',
                        fontWeight: '800',
                        color: '#6366f1',
                        letterSpacing: '0.5px'
                      }}>
                        AIF
                      </div>
                    )}
                  </div>
                  <div 
                    className="mt-2 text-teal-600 font-medium text-sm cursor-pointer hover:underline"
                    onClick={() => handleSchemeClick(fund)}
                  >
                    {fund.scheme_name}
                  </div>
                </div>

                {/* AUM, Rating, Compare */}
                <div className="p-4 bg-white border-b border-slate-200">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <div className="text-xs text-slate-500 font-medium mb-1">AUM (Cr)</div>
                      <div className="font-semibold text-slate-800">{formatAUM(fund.aum)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 font-medium mb-1">RATING</div>
                      <div className="flex items-center gap-1">
                        <StarRating rating={fund.rating || 0} />
                      </div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="text-xs text-slate-500 font-medium mb-2">COMPARE</div>
                      <button
                        onClick={() => handleCompareToggle(fund.amc_name + '-' + fund.scheme_name)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 ${
                          comparedItems[fund.amc_name + '-' + fund.scheme_name] ? 'bg-teal-600' : 'bg-slate-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            comparedItems[fund.amc_name + '-' + fund.scheme_name] ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Performance Metrics - Two Rows */}
                <div className="p-4">
                  <div className="text-xs text-slate-500 font-medium mb-3">PERFORMANCE</div>
                  
                  {/* Row 1: 1M, 3M, 6M, 1Y */}
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    <div className="text-center bg-slate-50 rounded-lg p-2">
                      <div className="text-xs text-slate-500 font-medium mb-1">1M</div>
                      <div className={`font-bold text-sm ${parseFloat(fund['1_month']) > 0 ? 'text-green-600' : parseFloat(fund['1_month']) < 0 ? 'text-red-600' : 'text-slate-400'}`}>
                        {formatPerformanceValue(fund['1_month']) || '-'}
                      </div>
                      {formatPerformanceValue(fund['benchmark_1_month']) && (
                        <div className="text-[10px] text-blue-600 mt-0.5">
                          vs {formatPerformanceValue(fund['benchmark_1_month'])}
                        </div>
                      )}
                    </div>
                    <div className="text-center bg-slate-50 rounded-lg p-2">
                      <div className="text-xs text-slate-500 font-medium mb-1">3M</div>
                      <div className={`font-bold text-sm ${parseFloat(fund['3_month']) > 0 ? 'text-green-600' : parseFloat(fund['3_month']) < 0 ? 'text-red-600' : 'text-slate-400'}`}>
                        {formatPerformanceValue(fund['3_month']) || '-'}
                      </div>
                      {formatPerformanceValue(fund['benchmark_3_month']) && (
                        <div className="text-[10px] text-blue-600 mt-0.5">
                          vs {formatPerformanceValue(fund['benchmark_3_month'])}
                        </div>
                      )}
                    </div>
                    <div className="text-center bg-slate-50 rounded-lg p-2">
                      <div className="text-xs text-slate-500 font-medium mb-1">6M</div>
                      <div className={`font-bold text-sm ${parseFloat(fund['6_month']) > 0 ? 'text-green-600' : parseFloat(fund['6_month']) < 0 ? 'text-red-600' : 'text-slate-400'}`}>
                        {formatPerformanceValue(fund['6_month']) || '-'}
                      </div>
                      {formatPerformanceValue(fund['benchmark_6_month']) && (
                        <div className="text-[10px] text-blue-600 mt-0.5">
                          vs {formatPerformanceValue(fund['benchmark_6_month'])}
                        </div>
                      )}
                    </div>
                    <div className="text-center bg-slate-50 rounded-lg p-2">
                      <div className="text-xs text-slate-500 font-medium mb-1">1Y</div>
                      <div className={`font-bold text-sm ${parseFloat(fund['1_year']) > 0 ? 'text-green-600' : parseFloat(fund['1_year']) < 0 ? 'text-red-600' : 'text-slate-400'}`}>
                        {formatPerformanceValue(fund['1_year']) || '-'}
                      </div>
                      {formatPerformanceValue(fund['benchmark_1_year']) && (
                        <div className="text-[10px] text-blue-600 mt-0.5">
                          vs {formatPerformanceValue(fund['benchmark_1_year'])}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Row 2: 2Y, 3Y, 5Y, SI */}
                  <div className="grid grid-cols-4 gap-2">
                    <div className="text-center bg-slate-50 rounded-lg p-2">
                      <div className="text-xs text-slate-500 font-medium mb-1">2Y</div>
                      <div className={`font-bold text-sm ${parseFloat(fund['2_year']) > 0 ? 'text-green-600' : parseFloat(fund['2_year']) < 0 ? 'text-red-600' : 'text-slate-400'}`}>
                        {formatPerformanceValue(fund['2_year']) || '-'}
                      </div>
                      {formatPerformanceValue(fund['benchmark_2_year']) && (
                        <div className="text-[10px] text-blue-600 mt-0.5">
                          vs {formatPerformanceValue(fund['benchmark_2_year'])}
                        </div>
                      )}
                    </div>
                    <div className="text-center bg-slate-50 rounded-lg p-2">
                      <div className="text-xs text-slate-500 font-medium mb-1">3Y</div>
                      <div className={`font-bold text-sm ${parseFloat(fund['3_year']) > 0 ? 'text-green-600' : parseFloat(fund['3_year']) < 0 ? 'text-red-600' : 'text-slate-400'}`}>
                        {formatPerformanceValue(fund['3_year']) || '-'}
                      </div>
                      {formatPerformanceValue(fund['benchmark_3_year']) && (
                        <div className="text-[10px] text-blue-600 mt-0.5">
                          vs {formatPerformanceValue(fund['benchmark_3_year'])}
                        </div>
                      )}
                    </div>
                    <div className="text-center bg-slate-50 rounded-lg p-2">
                      <div className="text-xs text-slate-500 font-medium mb-1">5Y</div>
                      <div className={`font-bold text-sm ${parseFloat(fund['5_year']) > 0 ? 'text-green-600' : parseFloat(fund['5_year']) < 0 ? 'text-red-600' : 'text-slate-400'}`}>
                        {formatPerformanceValue(fund['5_year']) || '-'}
                      </div>
                      {formatPerformanceValue(fund['benchmark_5_year']) && (
                        <div className="text-[10px] text-blue-600 mt-0.5">
                          vs {formatPerformanceValue(fund['benchmark_5_year'])}
                        </div>
                      )}
                    </div>
                    <div className="text-center bg-slate-50 rounded-lg p-2">
                      <div className="text-xs text-slate-500 font-medium mb-1">SI</div>
                      <div className={`font-bold text-sm ${parseFloat(fund['since_inception']) > 0 ? 'text-green-600' : parseFloat(fund['since_inception']) < 0 ? 'text-red-600' : 'text-slate-400'}`}>
                        {formatPerformanceValue(fund['since_inception']) || '-'}
                      </div>
                      {formatPerformanceValue(fund['benchmark_since_inception']) && (
                        <div className="text-[10px] text-blue-600 mt-0.5">
                          vs {formatPerformanceValue(fund['benchmark_since_inception'])}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <table className="amc-table hidden md:table">
            <thead>
              <tr>
                <th onClick={() => handleSort('amc_name')} className="cursor-pointer hover:bg-slate-50" style={{ width: '20%', position: 'sticky', left: 0, backgroundColor: '#f8fafc', zIndex: 10, boxShadow: '2px 0 4px rgba(0,0,0,0.1)' }}>
                  <div className="flex items-center gap-2 justify-start">
                    <span>AMC Name / Scheme</span>
                    {getSortIcon('amc_name')}
                  </div>
                </th>
                <th onClick={() => handleSort('rating')} className="cursor-pointer hover:bg-slate-50" style={{ width: '5%', paddingLeft: '1rem' }}>
                  <div className="flex items-center gap-2 justify-center">
                    <span>Rating</span>
                    {getSortIcon('rating')}
                  </div>
                </th>
                <th onClick={() => handleSort('aum')} className="cursor-pointer hover:bg-slate-50" style={{ width: '7%' }}>
                  <div className="flex items-center gap-2 justify-center">
                    <span>AUM (Cr)</span>
                    {getSortIcon('aum')}
                  </div>
                </th>
                <th onClick={() => handleSort('1_month')} className="cursor-pointer hover:bg-slate-50" style={{ width: '6%' }}>
                  <div className="flex items-center gap-2 justify-center">
                    <span>1M</span>
                    {getSortIcon('1_month')}
                  </div>
                </th>
                <th onClick={() => handleSort('3_month')} className="cursor-pointer hover:bg-slate-50" style={{ width: '6%' }}>
                  <div className="flex items-center gap-2 justify-center">
                    <span>3M</span>
                    {getSortIcon('3_month')}
                  </div>
                </th>
                <th onClick={() => handleSort('6_month')} className="cursor-pointer hover:bg-slate-50" style={{ width: '6%' }}>
                  <div className="flex items-center gap-2 justify-center">
                    <span>6M</span>
                    {getSortIcon('6_month')}
                  </div>
                </th>
                <th onClick={() => handleSort('1_year')} className="cursor-pointer hover:bg-slate-50" style={{ width: '6%' }}>
                  <div className="flex items-center gap-2 justify-center">
                    <span>1YR</span>
                    {getSortIcon('1_year')}
                  </div>
                </th>
                <th onClick={() => handleSort('2_year')} className="cursor-pointer hover:bg-slate-50" style={{ width: '6%' }}>
                  <div className="flex items-center gap-2 justify-center">
                    <span>2YR</span>
                    {getSortIcon('2_year')}
                  </div>
                </th>
                <th onClick={() => handleSort('3_year')} className="cursor-pointer hover:bg-slate-50" style={{ width: '6%' }}>
                  <div className="flex items-center gap-2 justify-center">
                    <span>3YR</span>
                    {getSortIcon('3_year')}
                  </div>
                </th>
                <th onClick={() => handleSort('5_year')} className="cursor-pointer hover:bg-slate-50" style={{ width: '6%' }}>
                  <div className="flex items-center gap-2 justify-center">
                    <span>5YR</span>
                    {getSortIcon('5_year')}
                  </div>
                </th>
                <th onClick={() => handleSort('since_inception')} className="cursor-pointer hover:bg-slate-50" style={{ width: '6%' }}>
                  <div className="flex items-center gap-2 justify-center">
                    <span className="whitespace-nowrap">SI</span>
                    {getSortIcon('since_inception')}
                  </div>
                </th>
                <th style={{ width: '8%' }}>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="compare-header-btn"
                    onClick={() => comparedCount > 0 && toast.info(`Comparing ${comparedCount} funds`)}
                  >
                    Compare
                  </Button>
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((fund, idx) => (
                <tr 
                  key={`${fund.amc_name}-${fund.scheme_name}-${idx}`}
                  style={fund.scheme_category === 'AIF' ? { 
                    borderLeft: '3px solid #6366f1'
                  } : {}}
                >
                  <td 
                    className="amc-name-cell" 
                    style={{ 
                      position: 'sticky', 
                      left: 0, 
                      backgroundColor: 'white', 
                      zIndex: 5, 
                      boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                      paddingLeft: fund.scheme_category === 'AIF' ? '8px' : undefined
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {fund.scheme_category === 'AIF' && (
                        <div style={{
                          border: '1.5px solid #6366f1',
                          borderRadius: '8px',
                          padding: '2px 2px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '16px',
                          height: '48px',
                          flexShrink: 0
                        }}>
                          <span style={{
                            color: '#6366f1',
                            fontSize: '7px',
                            fontWeight: '800',
                            letterSpacing: '0.5px',
                            writingMode: 'vertical-rl',
                            textOrientation: 'mixed',
                            transform: 'rotate(180deg)',
                            lineHeight: '1'
                          }}>
                            AIF
                          </span>
                        </div>
                      )}
                      <div style={{ flex: 1 }}>
                        <div className="amc-name">{fund.amc_name}</div>
                        <div 
                          className="scheme-name cursor-pointer hover:text-teal-600 hover:underline transition-colors"
                          onClick={() => handleSchemeClick(fund)}
                        >
                          {fund.scheme_name}
                        </div>
                      </div>
                    </div>
                    {fund.benchmark_name && (
                      <div style={{ fontSize: '0.6rem', color: '#94a3b8', fontStyle: 'italic', marginTop: '2px', lineHeight: '1.2' }}>
                        vs. {fund.benchmark_name.replace('Benchmark: ', '')}
                      </div>
                    )}
                  </td>
                  <td style={{ paddingLeft: '1rem' }}>
                    <div className="flex items-center justify-center">
                      <StarRating rating={fund.rating || 0} />
                    </div>
                  </td>
                  <td>{Math.round(parseFloat(fund.aum) || 0)}</td>
                  <td>
                    <div className={parseFloat(fund['1_month']) > 0 ? "positive" : parseFloat(fund['1_month']) < 0 ? "negative" : ""}>
                      {formatPerformanceValue(fund['1_month'])}
                    </div>
                    {formatPerformanceValue(fund.benchmark_1_month) && (
                      <div style={{ fontSize: '0.65rem', color: '#2563eb', marginTop: '2px' }}>
                        vs {formatPerformanceValue(fund.benchmark_1_month)}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className={parseFloat(fund['3_month']) > 0 ? "positive" : parseFloat(fund['3_month']) < 0 ? "negative" : ""}>
                      {formatPerformanceValue(fund['3_month'])}
                    </div>
                    {formatPerformanceValue(fund.benchmark_3_month) && (
                      <div style={{ fontSize: '0.65rem', color: '#2563eb', marginTop: '2px' }}>
                        vs {formatPerformanceValue(fund.benchmark_3_month)}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className={parseFloat(fund['6_month']) > 0 ? "positive" : parseFloat(fund['6_month']) < 0 ? "negative" : ""}>
                      {formatPerformanceValue(fund['6_month'])}
                    </div>
                    {formatPerformanceValue(fund.benchmark_6_month) && (
                      <div style={{ fontSize: '0.65rem', color: '#2563eb', marginTop: '2px' }}>
                        vs {formatPerformanceValue(fund.benchmark_6_month)}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className={parseFloat(fund['1_year']) > 0 ? "positive" : parseFloat(fund['1_year']) < 0 ? "negative" : ""}>
                      {formatPerformanceValue(fund['1_year'])}
                    </div>
                    {formatPerformanceValue(fund.benchmark_1_year) && (
                      <div style={{ fontSize: '0.65rem', color: '#2563eb', marginTop: '2px' }}>
                        vs {formatPerformanceValue(fund.benchmark_1_year)}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className={parseFloat(fund['2_year']) > 0 ? "positive" : parseFloat(fund['2_year']) < 0 ? "negative" : ""}>
                      {formatPerformanceValue(fund['2_year'])}
                    </div>
                    {formatPerformanceValue(fund.benchmark_2_year) && (
                      <div style={{ fontSize: '0.65rem', color: '#2563eb', marginTop: '2px' }}>
                        vs {formatPerformanceValue(fund.benchmark_2_year)}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className={parseFloat(fund['3_year']) > 0 ? "positive" : parseFloat(fund['3_year']) < 0 ? "negative" : ""}>
                      {formatPerformanceValue(fund['3_year'])}
                    </div>
                    {formatPerformanceValue(fund.benchmark_3_year) && (
                      <div style={{ fontSize: '0.65rem', color: '#2563eb', marginTop: '2px' }}>
                        vs {formatPerformanceValue(fund.benchmark_3_year)}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className={parseFloat(fund['5_year']) > 0 ? "positive" : parseFloat(fund['5_year']) < 0 ? "negative" : ""}>
                      {formatPerformanceValue(fund['5_year'])}
                    </div>
                    {formatPerformanceValue(fund.benchmark_5_year) && (
                      <div style={{ fontSize: '0.65rem', color: '#2563eb', marginTop: '2px' }}>
                        vs {formatPerformanceValue(fund.benchmark_5_year)}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className={parseFloat(fund.since_inception) > 0 ? "positive" : parseFloat(fund.since_inception) < 0 ? "negative" : ""}>
                      {formatPerformanceValue(fund.since_inception)}
                    </div>
                    {formatPerformanceValue(fund.benchmark_since_inception) && (
                      <div style={{ fontSize: '0.65rem', color: '#2563eb', marginTop: '2px' }}>
                        vs {formatPerformanceValue(fund.benchmark_since_inception)}
                      </div>
                    )}
                  </td>
                  <td>
                    <Switch 
                      checked={comparedItems[`${fund.amc_name}-${fund.scheme_name}`] || false}
                      onCheckedChange={() => handleCompareToggle(`${fund.amc_name}-${fund.scheme_name}`)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {filteredData.length === 0 && (
            <div className="no-results">
              <p>No funds found matching your criteria</p>
            </div>
          )}

          
          {/* Pagination Controls */}
          {filteredData.length > ITEMS_PER_PAGE && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-white">
              <div className="text-sm text-slate-600">
                Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, filteredData.length)} of {filteredData.length} schemes
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                <div className="flex items-center gap-2">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => {
                          analytics.trackPagination(pageNum);
                          setCurrentPage(pageNum);
                        }}
                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                          currentPage === pageNum
                            ? 'bg-purple-600 text-white'
                            : 'text-slate-700 bg-white border border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}

        </div>
      </main>

      {/* General Contact Modal */}
      {showGeneralContactModal && (
        <Suspense fallback={<div>Loading...</div>}>
          <GeneralContactModal 
            isOpen={showGeneralContactModal}
            onClose={() => setShowGeneralContactModal(false)}
          />
        </Suspense>
      )}

      {/* Calendly Modal */}
      <CalendlyModal 
        isOpen={showCalendlyModal}
        onClose={() => setShowCalendlyModal(false)}
      />

      {/* Fee Calculator Modal */}
      {showFeeCalculatorModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowFeeCalculatorModal(false);
            setFeeOptions({
              option1: { fixedFee: '', hurdle: '', performanceSharing: '' },
              option2: { fixedFee: '', hurdle: '', performanceSharing: '' },
              option3: { fixedFee: '', hurdle: '', performanceSharing: '' }
            });
            setFeeChartData([]);
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="bg-teal-100 p-3 rounded-full">
                  <Calculator className="w-6 h-6 text-teal-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-800">Fee Payoff Calculator</h2>
              </div>
              <button 
                onClick={() => {
                  setShowFeeCalculatorModal(false);
                  setFeeOptions({
                    option1: { fixedFee: '', hurdle: '', performanceSharing: '' },
                    option2: { fixedFee: '', hurdle: '', performanceSharing: '' },
                    option3: { fixedFee: '', hurdle: '', performanceSharing: '' }
                  });
                  setFeeChartData([]);
                }}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>
            </div>

            <p className="text-sm text-slate-600 mb-6">
              Enter fee structures for up to 3 options to compare fee payoffs across different return scenarios (-10% to 30%).
            </p>

            <div className="space-y-6">
              {/* Option 1 */}
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h3 className="text-md font-semibold text-slate-800 mb-3">Option 1</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs font-medium text-slate-700 flex items-center gap-1">
                      <Percent className="w-3 h-3" />
                      Fixed Fee
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="e.g., 2.0"
                      value={feeOptions.option1.fixedFee}
                      onChange={(e) => setFeeOptions({
                        ...feeOptions,
                        option1: { ...feeOptions.option1, fixedFee: e.target.value }
                      })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-700 flex items-center gap-1">
                      <Percent className="w-3 h-3" />
                      Hurdle
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="e.g., 6.0"
                      value={feeOptions.option1.hurdle}
                      onChange={(e) => setFeeOptions({
                        ...feeOptions,
                        option1: { ...feeOptions.option1, hurdle: e.target.value }
                      })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-700 flex items-center gap-1">
                      <Percent className="w-3 h-3" />
                      Perf. Sharing
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="e.g., 20.0"
                      value={feeOptions.option1.performanceSharing}
                      onChange={(e) => setFeeOptions({
                        ...feeOptions,
                        option1: { ...feeOptions.option1, performanceSharing: e.target.value }
                      })}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Option 2 */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h3 className="text-md font-semibold text-slate-800 mb-3">Option 2 <span className="text-xs text-slate-500">(Optional)</span></h3>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs font-medium text-slate-700 flex items-center gap-1">
                      <Percent className="w-3 h-3" />
                      Fixed Fee
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="e.g., 1.5"
                      value={feeOptions.option2.fixedFee}
                      onChange={(e) => setFeeOptions({
                        ...feeOptions,
                        option2: { ...feeOptions.option2, fixedFee: e.target.value }
                      })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-700 flex items-center gap-1">
                      <Percent className="w-3 h-3" />
                      Hurdle
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="e.g., 8.0"
                      value={feeOptions.option2.hurdle}
                      onChange={(e) => setFeeOptions({
                        ...feeOptions,
                        option2: { ...feeOptions.option2, hurdle: e.target.value }
                      })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-700 flex items-center gap-1">
                      <Percent className="w-3 h-3" />
                      Perf. Sharing
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="e.g., 25.0"
                      value={feeOptions.option2.performanceSharing}
                      onChange={(e) => setFeeOptions({
                        ...feeOptions,
                        option2: { ...feeOptions.option2, performanceSharing: e.target.value }
                      })}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Option 3 */}
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-4 rounded-lg border border-amber-200">
                <h3 className="text-md font-semibold text-slate-800 mb-3">Option 3 <span className="text-xs text-slate-500">(Optional)</span></h3>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs font-medium text-slate-700 flex items-center gap-1">
                      <Percent className="w-3 h-3" />
                      Fixed Fee
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="e.g., 2.5"
                      value={feeOptions.option3.fixedFee}
                      onChange={(e) => setFeeOptions({
                        ...feeOptions,
                        option3: { ...feeOptions.option3, fixedFee: e.target.value }
                      })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-700 flex items-center gap-1">
                      <Percent className="w-3 h-3" />
                      Hurdle
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="e.g., 5.0"
                      value={feeOptions.option3.hurdle}
                      onChange={(e) => setFeeOptions({
                        ...feeOptions,
                        option3: { ...feeOptions.option3, hurdle: e.target.value }
                      })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-700 flex items-center gap-1">
                      <Percent className="w-3 h-3" />
                      Perf. Sharing
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="e.g., 15.0"
                      value={feeOptions.option3.performanceSharing}
                      onChange={(e) => setFeeOptions({
                        ...feeOptions,
                        option3: { ...feeOptions.option3, performanceSharing: e.target.value }
                      })}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Calculate Button */}
              <Button 
                onClick={handleCalculateFees}
                className="w-full bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 text-white py-3 flex items-center justify-center gap-2"
              >
                <Calculator className="w-5 h-5" />
                Generate Fee Payoff Chart
              </Button>

              {/* Line Chart - Fee vs Return Payoff */}
              {feeChartData.length > 0 && (
                <div className="bg-gradient-to-br from-slate-50 to-blue-50 p-6 rounded-lg border border-blue-200">
                  <h3 className="text-lg font-semibold text-slate-800 mb-4">Fee Payoff Chart</h3>
                  <p className="text-xs text-slate-600 mb-4">
                    Shows how total fees change across different return scenarios (-10% to 30%)
                  </p>
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={feeChartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis 
                        dataKey="return"
                        type="number"
                        domain={[-10, 30]}
                        tick={{ fill: '#475569', fontSize: 12 }}
                        label={{ value: 'Expected Return (%)', position: 'insideBottom', offset: -10, style: { fill: '#475569', fontWeight: 600 } }}
                      />
                      <YAxis 
                        type="number"
                        domain={[0, 'auto']}
                        tick={{ fill: '#475569', fontSize: 12 }}
                        label={{ value: 'Total Fee (%)', angle: -90, position: 'insideLeft', style: { fill: '#475569', fontWeight: 600 } }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                        }}
                        formatter={(value, name) => [`${typeof value === 'number' ? value.toFixed(2) : value}%`, name]}
                        labelFormatter={(label) => `Return: ${label}%`}
                      />
                      <Legend wrapperStyle={{ paddingTop: '20px' }} />
                      {feeChartData[0].option1 !== undefined && (
                        <Line 
                          type="natural" 
                          dataKey="option1" 
                          stroke="#3B82F6" 
                          strokeWidth={3}
                          dot={false}
                          name="Option 1"
                        />
                      )}
                      {feeChartData[0].option2 !== undefined && (
                        <Line 
                          type="natural" 
                          dataKey="option2" 
                          stroke="#10B981" 
                          strokeWidth={3}
                          dot={false}
                          name="Option 2"
                        />
                      )}
                      {feeChartData[0].option3 !== undefined && (
                        <Line 
                          type="natural" 
                          dataKey="option3" 
                          stroke="#F59E0B" 
                          strokeWidth={3}
                          dot={false}
                          name="Option 3"
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Disclaimer */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-xs text-amber-800 text-center">
                  <strong>Disclaimer:</strong> This is for illustration only. Actual values and taxes could be different. Please read all scheme-related documents carefully.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <p className="footer-text">© 2025 PMS Kart. All rights reserved.</p>
          <div className="social-links">
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="social-link">
              <Linkedin className="w-5 h-5" />
            </a>
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="social-link">
              <Twitter className="w-5 h-5" />
            </a>
            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="social-link">
              <Facebook className="w-5 h-5" />
            </a>
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="social-link">
              <Instagram className="w-5 h-5" />
            </a>
            <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="social-link">
              <Youtube className="w-5 h-5" />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
